import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {injectIntl} from 'react-intl';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, {SelectOption} from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import {LEDControllerOverviewContext} from '../context/LEDControllerOverviewContext';
import VisualRackBuilder from './VisualRackBuilder';
import {
    ALLOWED_COLUMN_LABELS,
    COLUMN_TYPES,
    CONFIG_TYPES,
    getVisualColumns,
    parseAisleConfigName
} from './AisleConfigModel';
import {RACK_ZONE_COLORS, getZoneFill} from './AisleConfigColors';

const TIERS = [4, 3, 2, 1];
const OV_LETTER_OPTIONS = ALLOWED_COLUMN_LABELS.map((label) => label.toUpperCase());

const getMessage = (intl, id, defaultMessage, values) => intl.formatMessage({id, defaultMessage}, values);

// The model exposes machine-readable config types; the display wording is localized here.
const CONFIG_TYPE_MESSAGES = {
    [CONFIG_TYPES.BAG]: {id: 'aisleconfig.type.bag', defaultMessage: 'Bag rack'},
    [CONFIG_TYPES.OV]: {id: 'aisleconfig.type.ov', defaultMessage: 'OV rack'},
    [CONFIG_TYPES.FULL_HEIGHT_FLEX]: {id: 'aisleconfig.type.fullheightflex', defaultMessage: 'Full-height flex rack'},
    [CONFIG_TYPES.PARTITIONED_FLEX]: {id: 'aisleconfig.type.partitionedflex', defaultMessage: 'Partitioned flex rack'},
    [CONFIG_TYPES.LEGACY_DUAL]: {id: 'aisleconfig.type.legacydual', defaultMessage: 'Legacy dual-code rack'}
};

const getConfigTypeMessage = (intl, configType) => {
    const definition = CONFIG_TYPE_MESSAGES[configType] ||
        {id: 'aisleconfig.type.generic', defaultMessage: 'Aisle configuration'};
    return getMessage(intl, definition.id, definition.defaultMessage);
};

const styles = {
    panel: {
        border: '1px solid #d9e2e8',
        borderRadius: '12px',
        padding: '18px',
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 8px rgba(15, 41, 61, 0.08)'
    },
    metadataGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '12px'
    },
    metadataItem: {
        border: '1px solid #eaeded',
        borderRadius: '6px',
        padding: '10px',
        overflowWrap: 'anywhere'
    },
    tableContainer: {
        overflowX: 'auto',
        width: '100%'
    },
    rackTable: {
        borderCollapse: 'collapse',
        width: 'max-content',
        minWidth: '100%'
    },
    cell: {
        border: '1px solid #d5dbdb',
        minWidth: '74px',
        padding: '8px',
        textAlign: 'center'
    },
    tierCell: {
        border: '1px solid #d5dbdb',
        minWidth: '58px',
        padding: '8px',
        textAlign: 'center',
        fontWeight: 600,
        backgroundColor: '#f3f3f3'
    },
    bagCell: getZoneFill(RACK_ZONE_COLORS.BAG),
    ovCell: getZoneFill(RACK_ZONE_COLORS.OV),
    flexCell: getZoneFill(RACK_ZONE_COLORS.FLEX),
    flexCellCaption: {
        marginBottom: '4px',
        fontSize: '10px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        color: RACK_ZONE_COLORS.FLEX.color
    },
    pillarCell: {...getZoneFill(RACK_ZONE_COLORS.PILLAR), minWidth: '42px'},
    code: {
        display: 'block',
        padding: '8px',
        borderRadius: '4px',
        backgroundColor: '#f3f3f3',
        overflowWrap: 'anywhere'
    }
};

// Dispatch on column type via a lookup map rather than a long if-chain. The tier checks *inside*
// each renderer stay conditional on purpose: they decide which physical bin a tier row spans
// (rowSpan geometry), which is not a type dispatch and does not flatten into a map.
const TIER_CELL_RENDERERS = {
    [COLUMN_TYPES.PILLAR]: () => ({label: '\u2502', style: styles.pillarCell}),

    [COLUMN_TYPES.FLEX]: (intl, column, tier) => {
        const [topOvLabel = '?', bottomOvLabel = '?'] = column.ovLabels || [];
        if (tier === 4) {
            return {label: getMessage(intl, 'aisleconfig.preview.ovtop', '{label} (top OV)',
                {label: topOvLabel}), style: styles.flexCell, rowSpan: 2,
                testId: 'flex-ov-top', flexSlot: 0};
        }
        if (tier === 2) {
            return {label: getMessage(intl, 'aisleconfig.preview.ovbottom', '{label} (bottom OV)',
                {label: bottomOvLabel}), style: styles.flexCell, rowSpan: 2,
                testId: 'flex-ov-bottom', flexSlot: 1};
        }
        return {skip: true};
    },

    [COLUMN_TYPES.BAG]: (intl) =>
        ({label: getMessage(intl, 'aisleconfig.preview.bag', 'Bag'), style: styles.bagCell}),

    [COLUMN_TYPES.OV]: (intl, column, tier) => {
        const ovLabels = column.ovLabels || [column.label];
        // A split OV column is two stacked bins; a trailing unpaired letter is one full-height bin.
        if (ovLabels.length === 2) {
            if (tier === 4) {
                return {label: getMessage(intl, 'aisleconfig.preview.ovtop', '{label} (top OV)',
                    {label: ovLabels[0]}), style: styles.ovCell, rowSpan: 2,
                    testId: 'ov-top'};
            }
            if (tier === 2) {
                return {label: getMessage(intl, 'aisleconfig.preview.ovbottom', '{label} (bottom OV)',
                    {label: ovLabels[1]}), style: styles.ovCell, rowSpan: 2,
                    testId: 'ov-bottom'};
            }
            return {skip: true};
        }
        if (tier === 4) {
            return {label: getMessage(intl, 'aisleconfig.preview.ovfull', '{label} (full OV)',
                {label: ovLabels[0]}), style: styles.ovCell, rowSpan: 4,
                testId: 'ov-full'};
        }
        return {skip: true};
    },

    [COLUMN_TYPES.PARTITIONED_FLEX]: (intl, column, tier) => (column.bagTiers.includes(tier) ?
        {label: getMessage(intl, 'aisleconfig.preview.bag', 'Bag'), style: styles.bagCell} :
        {label: getMessage(intl, 'aisleconfig.preview.ov', 'OV'), style: styles.ovCell})
};

const getTierCell = (intl, column, tier) => {
    const renderTierCell = TIER_CELL_RENDERERS[column.type];
    return renderTierCell ? renderTierCell(intl, column, tier) : {label: '', style: {}};
};

const COLUMN_HEADER_RENDERERS = {
    [COLUMN_TYPES.PILLAR]: (intl) => getMessage(intl, 'aisleconfig.preview.pillar', 'Pillar'),

    [COLUMN_TYPES.FLEX]: (intl, column) => (
        <div data-testid="flex-footprint-header">
            <div>{getMessage(intl, 'aisleconfig.preview.flexfootprint', 'Flex footprint')}</div>
            <small>{getMessage(intl, 'aisleconfig.preview.bagmode', 'Bag mode: {labels}',
                {labels: (column.bagLabels || []).join(' + ')})}</small>
        </div>
    ),

    // An OV column with no resolved letters falls back to the plain column label.
    [COLUMN_TYPES.OV]: (intl, column) => ((column.ovLabels || []).length === 0 ? column.label : (
        <div data-testid="ov-column-header">
            <div>{column.label}</div>
            <small>{column.ovLabels.length === 2 ?
                getMessage(intl, 'aisleconfig.preview.splitov', 'Split OV') :
                getMessage(intl, 'aisleconfig.preview.fullov', 'Full OV')}</small>
        </div>
    ))
};

const getColumnHeader = (intl, column) => {
    const renderHeader = COLUMN_HEADER_RENDERERS[column.type];
    return renderHeader ? renderHeader(intl, column) : column.label;
};

export const RackPreview = ({parsedConfig, columns: providedColumns, onFlexOvLabelChange, intl}) => {
    const columns = providedColumns || getVisualColumns(parsedConfig);
    if (columns.length === 0) {
        return (
            <Alert type="informational">
                {getMessage(intl, 'aisleconfig.preview.notvisualizable',
                    'The selected filename does not contain a visualizable aisle code.')}
            </Alert>
        );
    }

    // Number the flex footprints in render order so inline edits target the right one.
    let flexOrdinal = -1;
    const decoratedColumns = columns.map((column) => {
        if (column.type === COLUMN_TYPES.FLEX) {
            flexOrdinal += 1;
            return {...column, flexIndex: flexOrdinal};
        }
        return column;
    });

    const renderFlexOvSelect = (column, slot) => (
        <div>
            <div style={styles.flexCellCaption}>
                {slot === 0 ?
                    getMessage(intl, 'aisleconfig.preview.topov', 'Top OV') :
                    getMessage(intl, 'aisleconfig.preview.bottomov', 'Bottom OV')}
            </div>
            <Select id={`flex-ov-${slot === 0 ? 'top' : 'bottom'}-${column.flexIndex}`}
                    value={(column.ovLabels || [])[slot]}
                    width={84}
                    onChange={(value) => onFlexOvLabelChange(column.flexIndex, slot, value)}>
                {OV_LETTER_OPTIONS.map((letter) =>
                    <SelectOption key={letter} value={letter} label={letter}/>)}
            </Select>
        </div>
    );

    return (
        <div style={styles.tableContainer} data-testid="rack-preview">
            <table style={styles.rackTable}
                   aria-label={getMessage(intl, 'aisleconfig.preview.arialabel', 'Aisle configuration preview')}>
                <thead>
                    <tr>
                        <th style={styles.tierCell}>
                            {getMessage(intl, 'aisleconfig.preview.tier', 'Tier')}
                        </th>
                        {decoratedColumns.map((column, index) => (
                            <th key={`${column.label}-${index}`} style={{...styles.cell,
                                ...(column.type === COLUMN_TYPES.PILLAR ? styles.pillarCell : {})}}>
                                {getColumnHeader(intl, column)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {TIERS.map((tier) => (
                        <tr key={tier}>
                            <td style={styles.tierCell}>{tier}</td>
                            {decoratedColumns.map((column, index) => {
                                const tierCell = getTierCell(intl, column, tier);
                                if (tierCell.skip) {
                                    return null;
                                }
                                const editableFlexCell = onFlexOvLabelChange &&
                                    column.type === COLUMN_TYPES.FLEX &&
                                    tierCell.flexSlot !== undefined;
                                return (
                                    <td key={`${tier}-${column.label}-${index}`}
                                        rowSpan={tierCell.rowSpan}
                                        data-testid={tierCell.testId}
                                        style={{...styles.cell, ...tierCell.style}}>
                                        {editableFlexCell ?
                                            renderFlexOvSelect(column, tierCell.flexSlot) :
                                            tierCell.label}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export const AisleConfigFinderView = ({configs, intl}) => {
    const [selectedConfig, setSelectedConfig] = useState('');
    const [copyStatus, setCopyStatus] = useState(null);
    const selectedParsed = useMemo(() => parseAisleConfigName(selectedConfig), [selectedConfig]);
    const detailsRef = useRef(null);

    useEffect(() => {
        if (selectedConfig && !configs.includes(selectedConfig)) {
            setSelectedConfig('');
        }
    }, [configs, selectedConfig]);

    // Bring the selected config's details into view — the panel renders below the builder
    // and would otherwise land off-screen after "Use matched config".
    useEffect(() => {
        if (selectedParsed && detailsRef.current &&
            typeof detailsRef.current.scrollIntoView === 'function') {
            detailsRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }, [selectedParsed]);

    const copyConfigName = async () => {
        setCopyStatus(null);
        const unavailableMessage = getMessage(intl, 'aisleconfig.copy.unavailable',
            'Clipboard access is unavailable. Select the config name and copy it manually.');
        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(selectedConfig);
                setCopyStatus({
                    type: 'success',
                    message: getMessage(intl, 'aisleconfig.copy.success', 'Config name copied.')
                });
            } catch (error) {
                setCopyStatus({type: 'informational', message: unavailableMessage});
            }
        } else {
            setCopyStatus({type: 'informational', message: unavailableMessage});
        }
    };

    return (
        <Column spacing="400">
            <VisualRackBuilder
                configs={configs}
                intl={intl}
                RackPreviewComponent={RackPreview}
                onSelectConfig={(value) => {
                    setSelectedConfig(value);
                    setCopyStatus(null);
                }}
            />

            {!selectedParsed &&
                <Alert type="informational">
                    {getMessage(intl, 'aisleconfig.selectprompt',
                        'Describe your rack above, then use a matched existing config to inspect its exact codes and layout.')}
                </Alert>}

            {selectedParsed && (
                <React.Fragment>
                    <div style={styles.panel} ref={detailsRef}>
                        <Column spacing="300">
                            <Row alignmentVertical="center" widths={["fill", "fit"]}>
                                <Text type="h500">{getMessage(intl, 'aisleconfig.details', 'Config details')}</Text>
                                <Button type="primary" size="small" onClick={copyConfigName}>
                                    {getMessage(intl, 'aisleconfig.copy', 'Copy config name')}
                                </Button>
                            </Row>
                            <code data-testid="config-name" style={styles.code}>{selectedConfig}</code>
                            {copyStatus && <Alert type={copyStatus.type} size="small">{copyStatus.message}</Alert>}
                            <div style={styles.metadataGrid}>
                                <div style={styles.metadataItem}>
                                    <Text type="b300">{getMessage(intl, 'aisleconfig.details.region', 'Region')}</Text><br/>
                                    {selectedParsed.region}</div>
                                <div style={styles.metadataItem}>
                                    <Text type="b300">{getMessage(intl, 'aisleconfig.details.year', 'Year')}</Text><br/>
                                    {selectedParsed.year ||
                                        getMessage(intl, 'aisleconfig.details.yearmissing', 'Not in filename')}</div>
                                <div style={styles.metadataItem}>
                                    <Text type="b300">{getMessage(intl, 'aisleconfig.details.layout', 'Layout')}</Text><br/>
                                    {getConfigTypeMessage(intl, selectedParsed.configType)}</div>
                                <div style={styles.metadataItem}>
                                    <Text type="b300">{getMessage(intl, 'aisleconfig.details.naming', 'Naming')}</Text><br/>
                                    {selectedParsed.namingConvention === 'legacy' ?
                                        getMessage(intl, 'aisleconfig.details.naminglegacy', 'Legacy production name') :
                                        getMessage(intl, 'aisleconfig.details.namingcanonical', 'Canonical name')}</div>
                            </div>
                            <Row widths={["fill", "fill"]} spacing="300">
                                <div style={styles.metadataItem}>
                                    <Text type="b300">
                                        {getMessage(intl, 'aisleconfig.details.bagcode', 'Primary / bag code')}
                                    </Text>
                                    <code style={styles.code}>{selectedParsed.aisleCode}</code>
                                </div>
                                {selectedParsed.alternateCode &&
                                    <div style={styles.metadataItem}>
                                        <Text type="b300">
                                            {getMessage(intl, 'aisleconfig.details.ovcode', 'Alternate / OV code')}
                                        </Text>
                                        <code style={styles.code}>{selectedParsed.alternateCode}</code>
                                    </div>}
                            </Row>
                            {selectedParsed.canonicalAisleCode !== selectedParsed.aisleCode &&
                                <Alert type="warning" size="small">
                                    {getMessage(intl, 'aisleconfig.details.legacytier',
                                        'Stored tier order is legacy. Canonical equivalent: {code}',
                                        {code: selectedParsed.canonicalAisleCode})}
                                </Alert>}
                            <Alert type="informational" size="small">
                                {getMessage(intl, 'aisleconfig.useconfiguration',
                                    'Copy this exact existing name and select it in the LED Controller Configuration tab.')}
                            </Alert>
                        </Column>
                    </div>
                    <div style={styles.panel}>
                        <Column spacing="300">
                            <Text type="h500">{getMessage(intl, 'aisleconfig.preview', 'Rack preview')}</Text>
                            <Row spacing="300">
                                <Text>{getMessage(intl, 'aisleconfig.legend.bag', 'Bag')}</Text>
                                <span style={{...styles.cell, ...styles.bagCell, minWidth: '32px', padding: '2px'}}> </span>
                                <Text>{getMessage(intl, 'aisleconfig.legend.ov', 'OV')}</Text>
                                <span style={{...styles.cell, ...styles.ovCell, minWidth: '32px', padding: '2px'}}> </span>
                                <Text>{getMessage(intl, 'aisleconfig.legend.flex', 'Flex')}</Text>
                                <span style={{...styles.cell, ...styles.flexCell, minWidth: '32px', padding: '2px'}}> </span>
                            </Row>
                            <RackPreview parsedConfig={selectedParsed} intl={intl}/>
                        </Column>
                    </div>
                </React.Fragment>
            )}
        </Column>
    );
};

export const AisleConfigFinder = ({intl}) => {
    const {viewModel, dispatch} = useContext(LEDControllerOverviewContext);

    useEffect(() => {
        if (viewModel.aisleTypes.size === 0) {
            dispatch({type: 'LOAD_AISLE_TYPES', data: {dispatcher: dispatch}});
        }
    }, []);

    return <AisleConfigFinderView configs={[...viewModel.aisleTypes]} intl={intl}/>;
};

export default injectIntl(AisleConfigFinder);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/aisleconfig/AisleConfigFinder.js